import apiClient from './apiClient';
import {
  Team,
  TeamWithMembers,
  TeamResponse,
  CreateTeamData,
  UpdateTeamData
} from '../types/team';

// Team leaderboard entry
export interface TeamLeaderboardEntry {
  teamId: number;
  teamName: string;
  teamDescription?: string;
  memberCount: number;
  totalScore: number;
  membersEnrolled: number;
  rank: number;
}

// Re-export the types so they can be imported from this service
export type { Team, TeamWithMembers, TeamResponse, CreateTeamData, UpdateTeamData };

// Get all teams with pagination (includeInactive for admins)
export const getAllTeams = async (page = 1, limit = 20, includeInactive = false): Promise<TeamResponse> => {
  try {
    const response = await apiClient.get(`/api/teams?page=${page}&limit=${limit}&includeInactive=${includeInactive}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching teams:', error);
    return {
      teams: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0
      }
    };
  }
};

// Get team by ID with members
export const getTeamById = async (teamId: number): Promise<TeamWithMembers | null> => {
  try {
    const response = await apiClient.get(`/api/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching team ${teamId}:`, error);
    return null;
  }
};

// Get teams for the current user
export const getMyTeams = async (): Promise<Team[]> => {
  try {
    const response = await apiClient.get('/api/teams/user/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching my teams:', error);
    return [];
  }
};

// Get teams for a specific user
export const getTeamsByUserId = async (userId: number): Promise<Team[]> => {
  try {
    const response = await apiClient.get(`/api/teams/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching teams for user ${userId}:`, error);
    return [];
  }
};

// Create a new team
export const createTeam = async (teamData: CreateTeamData): Promise<Team | null> => {
  try {
    const response = await apiClient.post('/api/teams', teamData);
    return response.data;
  } catch (error) {
    console.error('Error creating team:', error);
    throw error;
  }
};

// Update a team
export const updateTeam = async (teamId: number, teamData: UpdateTeamData): Promise<Team | null> => {
  try {
    const response = await apiClient.put(`/api/teams/${teamId}`, teamData);
    return response.data;
  } catch (error) {
    console.error(`Error updating team ${teamId}:`, error);
    throw error;
  }
};

// Join a team
export const joinTeam = async (teamId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(`/api/teams/${teamId}/join`);
    return response.data;
  } catch (error) {
    console.error(`Error joining team ${teamId}:`, error);
    throw error;
  }
};

// Leave a team
export const leaveTeam = async (teamId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(`/api/teams/${teamId}/leave`);
    return response.data;
  } catch (error) {
    console.error(`Error leaving team ${teamId}:`, error);
    throw error;
  }
};

// Remove a member from a team (captain/admin only)
export const removeMember = async (
  teamId: number,
  userId: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(`/api/teams/${teamId}/remove-member`, { userId });
    return response.data;
  } catch (error) {
    console.error(`Error removing member from team ${teamId}:`, error);
    throw error;
  }
};

// Update member role (captain/admin only)
export const updateMemberRole = async (
  teamId: number,
  userId: number,
  role: 'captain' | 'member'
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(`/api/teams/${teamId}/update-role`, { userId, role });
    return response.data;
  } catch (error) {
    console.error(`Error updating member role in team ${teamId}:`, error);
    throw error;
  }
};

// Delete a team (admin or creator only)
export const deleteTeam = async (teamId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.delete(`/api/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting team ${teamId}:`, error);
    throw error;
  }
};

// Get team leaderboard for a session
export const getTeamLeaderboard = async (sessionId: number): Promise<TeamLeaderboardEntry[]> => {
  try {
    const response = await apiClient.get(`/api/team-enrollments/sessions/${sessionId}/leaderboard`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching team leaderboard for session ${sessionId}:`, error);
    return [];
  }
};
